// BUILD YOUR MISSION — 3-step preliminary mission constructor for industry partners.
import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { windowState } from "@/lib/windows";

const PROVIDING = ["Product", "Funding", "Location", "Equipment", "Expertise", "Campaign Brief", "Opportunity"];
const WORLDS = ["Beauty", "Fashion", "Hospitality", "Entertainment", "Lifestyle", "Technology", "Art", "Food & Beverage", "Community", "Other"];
const OBJECTIVES = ["Brand Campaign", "Editorial", "Social Media Content", "Product Photography", "Video Campaign", "Event Activation", "Portfolio Project", "Educational Workshop", "Talent Search", "Open Creative Brief", "Not Sure — Let The Architect Decide"];
const ARCHITECT_DECIDES = OBJECTIVES[OBJECTIVES.length - 1];

const Pills = ({ options, value, onChange, testId }) => (
  <div className="flex flex-wrap gap-2" data-testid={testId}>
    {options.map((o) => (
      <button key={o} onClick={() => onChange(o)} type="button"
        className={`border px-3.5 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.15em] transition-all duration-200 ${
          value === o
            ? "border-gold bg-gradient-to-b from-goldlight to-gold text-onyx shadow-[0_0_20px_rgba(201,162,39,0.25)]"
            : "border-silver/30 bg-onyx text-silver/80 hover:border-gold/60 hover:text-goldlight"
        }`}>
        {o}
      </button>
    ))}
  </div>
);

const Step = ({ n, title, children }) => (
  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
    <p className="font-cinzel text-[10px] uppercase tracking-[0.4em] text-gold">Step {n}</p>
    <h3 className="mt-2 font-display text-2xl uppercase tracking-tight text-cream sm:text-3xl">{title}</h3>
    <div className="mt-4">{children}</div>
  </motion.div>
);

export const MissionBuilder = () => {
  const reduced = useReducedMotion();
  const [providing, setProviding] = useState(null);
  const [world, setWorld] = useState(null);
  const [objective, setObjective] = useState(null);
  const brandSealed = windowState("brand").status !== "open";

  const complete = providing && world && objective;
  const architectDecides = objective === ARCHITECT_DECIDES;

  const stash = () => {
    try {
      sessionStorage.setItem("blv_mission_params", JSON.stringify({ providing, world, objective }));
    } catch {}
  };

  return (
    <div className="space-y-10" data-testid="mission-builder">
      <Step n="1" title="What are you providing?">
        <Pills options={PROVIDING} value={providing} onChange={setProviding} testId="builder-providing" />
      </Step>
      {providing && (
        <Step n="2" title="Which creative world does it belong to?">
          <Pills options={WORLDS} value={world} onChange={setWorld} testId="builder-world" />
        </Step>
      )}
      {providing && world && (
        <Step n="3" title="What would you like to create?">
          <Pills options={OBJECTIVES} value={objective} onChange={setObjective} testId="builder-objective" />
        </Step>
      )}
      {complete && (
        <motion.div
          initial={{ opacity: 0, scale: reduced ? 1 : 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.45 }}
          className="gold-frame grain-dark relative bg-charcoal p-7 text-center sm:p-10" data-testid="mission-params-panel">
          <div className="pointer-events-none absolute inset-2 border border-gold/15" />
          <p className="font-mono text-[10px] uppercase tracking-[0.45em] text-gold">
            <span className="mr-2 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-gold align-middle" aria-hidden="true" />
            Mission parameters received
          </p>
          {architectDecides && (
            <p className="mt-4 font-cinzel text-xs uppercase tracking-[0.3em] text-cream/90 sm:text-sm" data-testid="architect-decides-line">
              The Architect will assemble the appropriate creative units.
            </p>
          )}
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            {[providing, world, objective].map((x) => (
              <span key={x} className="border border-gold/40 bg-onyx px-3 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-goldlight">{x}</span>
            ))}
          </div>
          <div className="gold-rule mx-auto mt-6 w-40" />
          <p className="mx-auto mt-5 max-w-md font-body text-sm leading-relaxed text-silver/80">
            Your organization may have exactly what one of our emerging creatives needs for their next mission.
          </p>
          <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link to="/apply/brand" onClick={stash} data-testid="submit-partnership-file-btn"
              className="btn-sweep inline-flex items-center gap-2 border border-gold bg-gradient-to-b from-goldlight to-gold px-8 py-3.5 font-head text-sm font-extrabold uppercase tracking-[0.2em] text-onyx transition-transform duration-200 hover:-translate-y-0.5">
              Submit Partnership File <ArrowRight size={15} />
            </Link>
            <Link to="/" className="border border-silver/40 px-8 py-3.5 font-head text-sm font-extrabold uppercase tracking-[0.2em] text-silver transition-colors hover:border-gold hover:text-goldlight">
              Return to the Village
            </Link>
          </div>
          {brandSealed && (
            <p className="mt-4 inline-block border border-gold/40 bg-onyx px-3 py-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-goldlight" data-testid="builder-sealed-chip">
              Network window opens {windowState("brand").opensLabel} — your parameters are saved for this session
            </p>
          )}
        </motion.div>
      )}
    </div>
  );
};
